import React from 'react'

// Top-level render-error boundary (ENG-559). The existing App-level overlay
// handles *async boot* failures (server unreachable, character/plugin load) with
// character-recovery actions; this catches *render-time* throws anywhere in the
// tree that would otherwise white-screen. The fallback is fully self-contained
// (inline styles, no imports from the crashed tree — DialogHost mounts inside
// App and may be gone) and offers a reload plus collapsible error details.

// Palette mirrors DialogHost's dark theme, inlined so the fallback never depends
// on the subtree that just crashed.
const C = {
  backdrop: '#0a0a0f',
  card: '#1a1d28',
  border: '#2a2c36',
  text: '#e1e1ed',
  dim: '#8f8fa2',
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render crash:', error, info?.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div
        role="alert"
        style={{
          position: 'fixed', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: C.backdrop, color: C.text,
          fontFamily: 'var(--m-font)', padding: 32, zIndex: 9000,
        }}
      >
        <div style={{ maxWidth: 560, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 24 }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>⚠</div>
          <div style={{ fontSize: 16, marginBottom: 8 }}>Something went wrong</div>
          <div style={{ color: C.dim, fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
            The app hit an unexpected error and couldn&apos;t continue rendering. Reloading usually fixes it.
          </div>
          <button
            type="button"
            onClick={() => { if (typeof window !== 'undefined') window.location.reload() }}
            style={{
              background: '#1d2b4a', border: '1px solid #2f4d86', color: '#cde0ff',
              borderRadius: 4, padding: '6px 14px', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13,
            }}
          >
            Reload
          </button>
          <details style={{ marginTop: 16, color: C.dim, fontSize: 12 }}>
            <summary style={{ cursor: 'pointer' }}>Error details</summary>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 8 }}>
              {String(error?.message ?? error)}
              {error?.stack ? `\n\n${error.stack}` : ''}
            </pre>
          </details>
        </div>
      </div>
    )
  }
}
